// Homepage banner — hero slideshow (sections/homepage-banner.liquid).
// Scroll-snap track (native swipe on touch) + dot navigation; the dots only
// render with 2+ slides. Optional autoplay via [data-autoplay] (ms), paused on
// hover / pointer interaction / hidden tab and disabled entirely under
// prefers-reduced-motion.
//
// Also drives hero video playback (see playVisibleHeroVideos at the bottom), so
// the section loads this file for a single-slide banner that has a video too.
class BannerSlideshow extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-hpb-track]');
    this.slides = Array.from(this.querySelectorAll('[data-hpb-slide]'));
    this.dots = Array.from(this.querySelectorAll('[data-hpb-dot]'));
    if (!this.track || this.slides.length < 2) return;

    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.index = 0;

    this.dots.forEach((dot, i) => {
      dot.addEventListener('click', () => {
        this.goTo(i);
        this.stopAutoplay(); // user took control
      });
    });

    this._onScroll = () => {
      window.clearTimeout(this._scrollTimer);
      this._scrollTimer = window.setTimeout(() => this.syncDots(), 80);
    };
    this.track.addEventListener('scroll', this._onScroll, { passive: true });

    // Autoplay (opt-in via setting).
    const interval = parseInt(this.dataset.autoplay || '0', 10);
    if (interval > 0 && !this.reduceMotion) {
      this.startAutoplay(interval);
      this.addEventListener('mouseenter', () => this.pauseAutoplay());
      this.addEventListener('mouseleave', () => this.resumeAutoplay());
      this.track.addEventListener('pointerdown', () => this.stopAutoplay(), { passive: true });
      document.addEventListener('visibilitychange', (this._onVis = () => {
        if (document.hidden) this.pauseAutoplay();
        else this.resumeAutoplay();
      }));
    }
  }

  disconnectedCallback() {
    this.stopAutoplay();
    if (this._onVis) document.removeEventListener('visibilitychange', this._onVis);
  }

  goTo(i) {
    const n = this.slides.length;
    this.index = ((i % n) + n) % n;
    this.track.scrollTo({
      left: this.index * this.track.clientWidth,
      behavior: this.reduceMotion ? 'auto' : 'smooth',
    });
    this.markActive(this.index);
  }

  syncDots() {
    const i = Math.round(this.track.scrollLeft / this.track.clientWidth);
    if (i !== this.index) {
      this.index = i;
      this.markActive(i);
    }
  }

  markActive(i) {
    this.dots.forEach((dot, d) => {
      dot.classList.toggle('is-active', d === i);
      if (d === i) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });
  }

  startAutoplay(interval) {
    this._interval = interval;
    this._timer = window.setInterval(() => this.goTo(this.index + 1), interval);
  }

  pauseAutoplay() {
    if (this._timer) {
      window.clearInterval(this._timer);
      this._timer = null;
    }
  }

  resumeAutoplay() {
    if (this._interval && !this._timer && !this._stopped) {
      this.startAutoplay(this._interval);
    }
  }

  stopAutoplay() {
    this._stopped = true;
    this.pauseAutoplay();
  }
}

if (!customElements.get('banner-slideshow')) {
  customElements.define('banner-slideshow', BannerSlideshow);
}


/*
 * Hero videos play from here rather than from an `autoplay` attribute.
 *
 * A slide carries up to two video slots, desktop and mobile, and CSS displays
 * only the one the viewport uses. Chrome will happily autoplay a display:none
 * video, so an attribute would stream both — real cost on a phone for a file
 * nobody sees. Instead: play the slot that is actually laid out, pause the other,
 * and redo that when the breakpoint changes.
 *
 * Leaving playback to JS also gives prefers-reduced-motion for free: nothing is
 * played, and Shopify's own first-frame poster stays up as the hero image.
 */
function playVisibleHeroVideos() {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  document.querySelectorAll('.hpb__video').forEach((video) => {
    // offsetParent is null for a display:none element — the cheapest way to ask
    // "is this the slot the current breakpoint uses?".
    const visible = video.offsetParent !== null;

    if (!visible) {
      if (!video.paused) video.pause();
      return;
    }
    if (!video.paused) return;

    video.muted = true; // belt and braces: a non-muted video is never allowed to autoplay
    video.playsInline = true;
    const played = video.play();
    // Blocked (a strict autoplay policy, a data-saver mode): the poster stays,
    // which is exactly the fallback we want. Nothing to do.
    if (played && typeof played.catch === 'function') played.catch(() => {});
  });
}

function watchHeroVideos() {
  playVisibleHeroVideos();
  let resizeTimer = null;
  window.addEventListener(
    'resize',
    () => {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(playVisibleHeroVideos, 150);
    },
    { passive: true }
  );
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', watchHeroVideos, { once: true });
} else {
  watchHeroVideos();
}

// The theme editor re-renders the whole section on every setting change.
document.addEventListener('shopify:section:load', (event) => {
  if (event.target?.querySelector('.hpb__video')) playVisibleHeroVideos();
});
